import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { AxiosResponse } from 'axios'
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { KForm, KFormInput, KFormDatetime, KFormDatepicker, KFormCheckbox, KFormSelect, KFormAddressSearch, KFormEditor, KFormSpinner, KFormCheckboxGroup } from '@kwixeo/form-vue'
import { BFormGroup, BFormRow, BCol, BCard, BCardHeader, BCollapse, BCardBody, BRow, BButton, BFormRadio, VBToggle } from 'bootstrap-vue'
import CalendarEventDelete from '../CalendarEventDelete/CalendarEventDelete.vue'
import CalendarElementSearch from '../CalendarElementSearch/CalendarElementSearch.vue'
import editorConfig from './editorConfig'

@Component({
  computed: {
    ...mapState('calendars', ['calendars']),
    ...mapGetters('user', ['teamUserConfigs'])
  },
  methods: {
    ...mapActions('calendars', ['loadCalendars'])
  },
  components: {
    CalendarEventDelete, CalendarElementSearch,
    KForm, KFormInput, KFormDatetime, KFormDatepicker, KFormCheckbox, KFormSelect, KFormAddressSearch, KFormEditor, KFormSpinner, KFormCheckboxGroup,
    BFormGroup, BFormRow, BCol, BCard, BCardHeader, BCollapse, BCardBody, BRow, BButton, BFormRadio
  },
  directives: {
    'b-toggle': VBToggle
  }
})
export default class CalendarEventForm extends Vue {
  loadCalendars!: (force?: boolean) => void
  calendars!: KwixeoCalendars.Calendars
  teamUserConfigs!: KwixeoInterface.GenericObject

  @Prop({ default: null }) value!: KwixeoCalendars.Event|null
  @Prop({ default: false }) askDelete!: boolean

  private event: KwixeoCalendars.FormEvent = {
    all_day: false,
    elements: []
  }
  private customColor = false
  private withReccurence = false
  private endReccurenceMode: 1|2|3 = 1
  private toDelete: null|KwixeoCalendars.Event = null
  private editorConfig = editorConfig

  async created (): Promise<void> {
    await this.loadCalendars()
    this.setLocalValue(this.value)
  }

  get computedCalendars (): KwixeoCalendars.Calendars {
    return this.calendars.filter(calendar => !calendar.readonly)
  }

  get recurrenceTypes (): KwixeoCalendars.SelectParams {
    return [
      { id: 'day', label: this.$t('calendars.event.days') },
      { id: 'week', label: this.$t('calendars.event.weeks') },
      { id: 'month', label: this.$t('calendars.event.months') },
      { id: 'year', label: this.$t('calendars.event.years') }
    ]
  }

  get formUrl (): string {
    let url = '/calendar/'
    if (!this.event.id && this.withReccurence) {
      return url + '/recurrence'
    }
    url += 'event'
    if (this.event.id) {
      url += '/' + this.event.id
    }
    return url
  }

  get formMethod (): string {
    return this.event.id ? 'PUT' : 'POST'
  }

  get beginDate (): string|null {
    const begin = this.event.begin
    if (!begin) {
      return null
    }
    return begin.substr(0, 10)
  }

  get endDate (): string|null{
    const end = this.event.end
    if (!end) {
      return null
    }
    return end.substr(0, 10)
  }

  private updateBeginDate (date: string) {
    this.event.begin = date + ' 00:00:00'
  }

  private setLocalValue (event: KwixeoCalendars.Event|null): void {
    const local = Object.assign({
      elements: [],
      repeat_type: 'week'
    },event ? JSON.parse(JSON.stringify(event)) : {})

    if (local.begin) {
      local.begin = moment(local.begin).format('YYYY-MM-DD HH:mm:ss')
    }

    if (local.end) {
      local.end = moment(local.end).format('YYYY-MM-DD HH:mm:ss')
    }

    if (!local.calendar) {
      if (this.teamUserConfigs.calendar_default_calendar) {
        local.calendar = this.teamUserConfigs.calendar_default_calendar
      } else if (this.computedCalendars.length > 0) {
        local.calendar = this.computedCalendars[0]
      }
    }

    if (!local.id) {
      local.repeat_value = 1
      local.days = [moment(local.begin).day()]
      local.nb_occurence = 1
    }

    this.event = local

    if (this.event.color || this.event.foreground_color) {
      this.customColor = true
    }
    this.applyCalendarColors()
  }

  private updateEndDate (date: string): void {
    this.event.end = date + ' 00:00:00'
  }

  private applyCalendarColors (): void {
    if (this.customColor) {
      return
    }
    const calendar = this.event.calendar as KwixeoCalendars.Calendar
    if (calendar) {
      this.event.color = calendar.color
      this.event.foreground_color = calendar.foregroundColor
    }
  }

  private submitComplete (response: AxiosResponse): void {
    this.$emit('input', response.data)
  }

  @Watch('askDelete')
  private watchAskDelete (state: boolean): void {
    if (state && this.event.id) {
      this.toDelete = this.event as KwixeoCalendars.Event
    }
  }
}
